<template>
  <CopyTrackWrapper
    type="goal"
    :title="pageContent.title"
    :uuid="pageContent.uuid"
    :demo="pageContent.demo"
    class="content-page"
  >
    <PremiumRequired v-if="premiumBlock" />
    <div v-else class="container">
      <ContentPageBreadcrumbs
        v-if="pageContent.uuid"
        :subject="breadcrumbSubject || pageContent.subject"
        :subject-domains="(breadcrumbDomain && [breadcrumbDomain]) || pageContent.subject_domains"
        :grades="pageContent.grades"
        :title="pageContent.title"
      />

      <div class="row">
        <div class="col-sm-8">
          <h1>
            {{ pageContent.title }}
            <small
              class="text-muted"
              v-if="pageContent.uuid && !pageContent.published"
            >(Draft)</small
            >
            <a
              v-if="editorVisibility"
              :href="editGoalUrl"
              class="btn btn-sm btn-default"
            ><i class="fa fa-pencil" /> Edit</a
            >
          </h1>

          <BigGoal
            :footer="
              pageContent.goal_example_text && pageContent.goal_example_text.length > 0
                ? true
                : false
            "
            :header="goalHeaderText"
            :event-context="{ adapted_goal_level: 'grade' }"
          >
            <CopyTrackWrapper
              slot="body"
              type="goal"
              :title="pageContent.title"
              :uuid="pageContent.uuid"
              :level="'Grade Level'"
              :demo="pageContent.demo"
            >
              <template v-if="!accessInfo.blur">
                <EditableWikiTextArea
                  v-model="pageContent.goal_text"
                  :strategies="strategyMap"
                  v-if="pageContent.goal_text"
                  @textEdited="
                    handleGoalTextEdited({ level: 'grade', text: pageContent.goal_text })
                  "
                  @textReset="
                    resetGoalTextForLevel({ level: 'grade', text: pageContent.goal_text })
                  "
                />
              </template>
              <template v-if="accessInfo.blur">
                <WikiText
                  :text="pageContent.goal_text"
                  :strategies="strategyMap"
                  :locked="accessInfo.blur"
                  lock-mode="always_snippet_first_block"
                  :plaintext="accessInfo.blur"
                />
              </template>
            </CopyTrackWrapper>
            <template v-slot:footer v-if="pageContent.goal_example_text">
              <GoalExampleContent @toggle="clickedExampleContent">
                <CopyTrackWrapper
                  type="goal-example"
                  :title="pageContent.title"
                  :uuid="pageContent.uuid"
                  :demo="pageContent.demo"
                >
                  <WikiText :text="pageContent.goal_example_text" :locked="accessInfo.blur" />
                </CopyTrackWrapper>
              </GoalExampleContent>
            </template>
          </BigGoal>

          <div class="row" v-if="accessInfo.blur">
            <div class="col-sm-10 col-sm-offset-1">
              <limit-access-alert :access-info="accessInfo" />
            </div>
          </div>

          <AdaptedGoalsAccordion
            v-if="filteredAdaptedGoals"
            :goal-uuid="pageContent.uuid"
            :goal-title="pageContent.title"
            :subject="pageContent.subject"
            :adapted-goals="filteredAdaptedGoals"
            :strategies="strategyMap"
            :access-info="accessInfo"
            :initial-expanded-level="expandedLevel"
            @adaptedGoal:edited="handleGoalTextEdited"
            @adaptedGoal:reset="resetGoalTextForLevel"
            @adaptedGoal:paneltoggle="trackAdaptedGoalClick"
          />

          <h2>UDL-Aligned Strategies <small><a
            :href="aboutUDLLink"
            target="_blank"
            title="About Universal Design for Learning">About UDL <OpensInNewTabIcon :external-link="true" /> </a></small></h2>
          <UdlAdaptationPanelGroup
            :udl-sections="udlAdaptations"
            :locked="accessInfo.blur"
            :strategies="strategyMap"
          />
        </div>
        <div class="col-sm-4">
          <div class="row">
            <div class="col-sm-12 col-xs-6">
              <SaveToLibraryButton
                :content-uuid="pageContent.uuid"
                :content-payload="savedGoalPayloadWithContentProfile"
                :user-uuid="userUuid"
                :action-permissions="actionPermissions.save"
                @saved:start="resetSaveStatus"
                @saved:error="handleSaveError"
                @saved:success="handleSaveSuccess"
              />

              <transition name="fade">
                <alert
                  type="success"
                  class="action-alert text-center"
                  :dismissible="true"
                  @dismissed="resetSaveStatus"
                  :duration="5000"
                  v-if="saveToLibraryStatus.savedSuccessInfo.name"
                >
                  Saved to
                  <span class="fs-block">{{ saveToLibraryStatus.savedSuccessInfo.name }}</span
                  >!
                  <router-link :to="getSavedGoalLink(saveToLibraryStatus.savedSuccessInfo.uuid)"
                  >View saved goal</router-link
                  >
                </alert>
              </transition>
              <transition name="fade">
                <alert
                  type="danger"
                  class="action-alert text-center"
                  :dismissible="true"
                  @dismissed="resetSaveStatus"
                  v-if="saveToLibraryStatus.savedErrorMessage"
                >
                  {{ saveToLibraryStatus.savedErrorMessage }}
                </alert>
              </transition>
            </div>
          </div>

          <ProgressMonitoringPanel
            v-if="pageContent.title && pageContent.uuid"
            :content-title="pageContent.title"
            :content-uuid="pageContent.uuid"
            :content-type="'goal'"
          />

          <template v-if="fetchingStandards || fetchingStaircase">
            <p class="text-muted"><i class="fa fa-spin fa-circle-o-notch" /> Loading...</p>
          </template>
          <GoalContentPageStandards
            :show-edit-link="editorVisibility"
            :content-profile-id="profileId"
            :grouped-standards="visibleGroupedStandardsBySetCode"
          />

          <template v-if="staircaseContent && staircaseContent.length > 0">
            <h2>Standard Staircase</h2>
            <CopyTrackWrapper
              type="staircase"
              :title="pageContent.title"
              :uuid="pageContent.uuid"
              :demo="pageContent.demo"
            >
              <Staircase
                :page-uuid="pageContent.uuid"
                :grades="pageContent.grades"
                :goal-staircase="staircaseContent"
              />
            </CopyTrackWrapper>
          </template>

          <template v-if="strategies && strategies.length > 0">
            <h2>Referenced Strategies</h2>
            <StrategyMediaList :strategies="strategies" />
          </template>
        </div>
      </div>

      <SubmitFeedback
        :title="pageContent.title"
        :uuid="pageContent.uuid"
        type="Goal Detail Page"
        :subject="pageContent.subject"
      />
    </div>

    <div class="print-message">
      This page is intentionally left blank. To print a goal from your Library, view the saved
      version of the goal and click on "Print this goal."
    </div>
  </CopyTrackWrapper>
</template>

<script>
import {
  goalPageMixin,
  saveCustomContentWithContentProfileMixin,
  trackGoalContentEditedMixin,
} from '@/components/goals/common/goal_content_mixin'
import ContentPageBreadcrumbs from '@/components/goals/ContentPageBreadcrumbs'
import AdaptedGoalsAccordion from '@/components/goals/AdaptedGoalsAccordion'
import ProgressMonitoringPanel from '@/components/goals/ProgressMonitoringPanel'
import EditableWikiTextArea from '@/components/EditableWikiTextArea'
import UdlAdaptationPanelGroup from '@/components/goals/UdlAdaptationPanelGroup'
import SaveToLibraryButton from '@/components/goals/SaveToLibraryButton'
import LimitAccessAlert from '@/components/LimitAccessAlert'
import StrategyMediaList from '@/components/strategies/StrategyMediaList'
import PremiumRequired from '@/components/PremiumRequired'

import { toolkitUrls, goalbookUrls } from '@/links'
import { mapGetters, mapState, mapMutations } from 'vuex'
import { CONTENT_TYPES } from '@/util/access_visibility'
import { setTitle } from '@/util/title'
import { setOrAppendNoIndexMetaTag } from '@/util/noindex'
import OpensInNewTabIcon from '../OpensInNewTabIcon.vue'

export default {
  name: 'GoalContentPage',
  mixins: [goalPageMixin, saveCustomContentWithContentProfileMixin, trackGoalContentEditedMixin],
  components: {
    ContentPageBreadcrumbs,
    AdaptedGoalsAccordion,
    EditableWikiTextArea,
    UdlAdaptationPanelGroup,
    SaveToLibraryButton,
    LimitAccessAlert,
    StrategyMediaList,
    ProgressMonitoringPanel,
    PremiumRequired,
    OpensInNewTabIcon,
  },
  props: {
    identifier: {
      type: String,
      required: true,
    },
    identifierAttribute: {
      type: String,
      required: true,
      validator(value) {
        return ['uuid', 'slug'].indexOf(value) > -1
      },
    },
    expandedLevel: {
      type: String,
      default: '',
    },
  },
  eventContext() {
    return {
      goal_breadcrumb_subject: this.breadcrumbSubject || '',
      goal_breadcrumb_domain: this.breadcrumbDomain || '',
      goal_subject_alignments: this.pageContent.subject_alignments,
      goal_subject: this.pageContent.subject,
      goal_subject_domains: this.pageContent.subject_domains,
      goal_grades: this.pageContent.grades,
      goal_uuid: this.pageContent.uuid,
      goal_title: this.pageContent.title,
      goal_standards:
        this.pageContent.standard_set_refs &&
        this.pageContent.standard_set_refs.map((setRef) => setRef.standard_long_ref),
      goal_author_id: this.pageContent.author_id,
      goal_is_anchor: this.pageContent.super_page,
      goal_is_library: false,
    }
  },
  data() {
    return {
      aboutUDLLink: goalbookUrls.aboutUDL,
    }
  },
  computed: {
    ...mapGetters('user', ['authInfo', 'isDefault']),
    ...mapState('user', { userUuid: (state) => state.user.uuid }),

    ...mapGetters('goaldetail', ['udlAdaptations', 'premiumBlock']),
    ...mapState('goaldetail', ['saveToLibraryStatus', 'strategies']),
    ...mapGetters('customgoal', ['savedGoalPayload']),
    ...mapGetters('visibility', ['editorVisibility', 'betaVisibility']),

    savedGoalPayloadWithContentProfile() {
      return this.contentPayloadWithContentProfile(this.savedGoalPayload)
    },

    filteredAdaptedGoals() {
      return (
        this.pageContent &&
        this.pageContent.objectives &&
        this.pageContent.objectives.filter((obj) => obj.level && obj.text)
      )
    },
    editGoalUrl() {
      return toolkitUrls.editorLinks.editGoal(this.pageContent.uuid)
    },
  },
  methods: {
    ...mapMutations('goaldetail', [
      'setSaveToLibrarySuccess',
      'setSaveToLibraryError',
      'resetSaveStatus',
    ]),
    ...mapMutations('customgoal', {
      resetGoalTextForLevel: 'resetGoalText',
    }),

    loadPageContent() {
      this.fetchPageContent({
        type: CONTENT_TYPES.GOAL,
        attribute: this.identifierAttribute,
        value: this.identifier,
      }).then(() => {
        setTitle(`${this.pageContent.title} | ${this.pageContent.subject} IEP Goal`)
        if (!this.pageContent.published) {
          setOrAppendNoIndexMetaTag()
        }
        this.initializeCustomGoal({
          contentUuid: this.pageContent.uuid,
          primaryGoalText: this.pageContent.goal_text,
          adaptedGoalsArray: this.filteredAdaptedGoals,
        })
        this.$sendEvent('Viewed Goal', {
          standard_codes:
            this.pageContent.standard_set_refs &&
            this.pageContent.standard_set_refs.map((setRef) => setRef.standard_long_ref),
        })
      })
    },
    handleSaveSuccess(savedGoal) {
      this.setSaveToLibrarySuccess({
        name: savedGoal.student_name,
        uuid: savedGoal.uuid,
      })
    },
    getSavedGoalLink(uuid) {
      return toolkitUrls.libraryGoalPage(uuid)
    },
    handleSaveError(errorMessage) {
      this.setSaveToLibraryError(errorMessage)
    },
    trackAdaptedGoalClick({ level, collapsed }) {
      this.$sendEvent(
        'Clicked Adapted Goal',
        {
          adapted_goal_level: level,
          level,
          collapsed,
        },
        {
          ct: this.pageContent.title,
          cu: this.pageContent.uuid,
        }
      )
    },
  },
  watch: {
    $route: function () {
      this.loadPageContent()
    },
  },
  created() {
    this.loadPageContent()
  },
}
</script>

<style lang="less"></style>
